import { Link } from "@tanstack/react-router";
import { ChevronDown, LogOut, ShieldCheck, User, UserCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslations } from "@/i18n/IntlProvider";
import type { MeResponse } from "@/auth/me";

/**
 * Header-right user menu — ported 1:1 from the legacy
 * web/src/components/auth/auth-menu.tsx so the visual contract stays
 * identical to the pre-ADR-027 surface:
 *
 *   - Anonymous: a single Anmelden link with a placeholder avatar
 *     (no separate sign-up CTA; Keycloak's login page hosts the
 *     "Registrieren"-Link).
 *   - Logged-in: avatar + email act as a Radix dropdown trigger.
 *     The dropdown lists "Mein Konto", an optional Admin shortcut
 *     for `system-admin` principals, and Abmelden.
 *
 * Differences from the Next.js original:
 *   - No `OidcInitializationGate` — under the BFF flow the principal
 *     is resolved at route-level (loaders) and arrives via the
 *     `<AppShell>` props, so there's no first-paint flash to suppress.
 *   - Logout is a regular link to `/$locale/logout`, which then POSTs
 *     to `/auth/logout` with the CSRF token and bounces to the
 *     marketing origin. Same UX, different transport.
 */
export function AuthMenu({
  lang,
  principal,
}: {
  lang: string;
  principal: MeResponse | null;
}) {
  const t = useTranslations("auth");

  if (!principal) {
    return (
      <Link
        to={"/$locale/login" as never}
        params={{ locale: lang } as never}
        className="inline-flex h-9 items-center gap-2 rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <span
          className="inline-flex size-7 items-center justify-center rounded-full bg-muted text-muted-foreground"
          aria-hidden
        >
          <User className="size-4" />
        </span>
        {t("login.cta")}
      </Link>
    );
  }

  const email = principal.email ?? "";
  const initial = email.charAt(0).toUpperCase() || "?";
  const isAdmin = principal.roles?.includes("system-admin") ?? false;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-9 items-center gap-2 rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-foreground"
        aria-label={t("account.title")}
      >
        <span
          className="inline-flex size-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
          aria-hidden
        >
          {initial}
        </span>
        <span className="hidden max-w-[160px] truncate sm:inline">{email}</span>
        <ChevronDown className="size-4 shrink-0" aria-hidden />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={6} className="min-w-[220px]">
        <DropdownMenuItem asChild>
          <Link
            to={"/$locale/settings/account" as never}
            params={{ locale: lang } as never}
            className="flex cursor-pointer items-center gap-2"
          >
            <UserCircle className="size-4" aria-hidden />
            {t("account.title")}
          </Link>
        </DropdownMenuItem>
        {isAdmin ? (
          <DropdownMenuItem asChild>
            <Link
              to={"/$locale/admin/accounts" as never}
              params={{ locale: lang } as never}
              className="flex cursor-pointer items-center gap-2"
            >
              <ShieldCheck className="size-4" aria-hidden />
              Admin
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to={"/$locale/logout" as never}
            params={{ locale: lang } as never}
            className="flex cursor-pointer items-center gap-2"
          >
            <LogOut className="size-4" aria-hidden />
            {t("logout.cta")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
